import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CheckCircle2, MapPin, Heart, Users, Megaphone, PartyPopper, Package, Award, Upload, Mail, Clock, Shield, Sparkles, ArrowRight, Star, Gift } from 'lucide-react';
import { sendViaWeb3Forms } from '../services/web3formsService';
import { saveSubmission, type BenevoleSubmission } from '../services/databaseService';
export function BenevolesPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries()) as Record<string, string>;
    // Sauvegarder dans la base de données locale
    saveSubmission<BenevoleSubmission>({
      type: 'benevole',
      nom: data.nom || '',
      prenom: data.prenom || '',
      email: data.email || '',
      telephone: data.telephone || '',
      quartier: data.quartier || '',
      disponibilite: data.disponibilite || '',
      motivation: data.motivation || ''
    });
    // Envoyer via Web3Forms (email à contact@adcc.africa)
    const result = await sendViaWeb3Forms({
      type: 'benevole',
      fields: data
    });
    console.log('📋 Bénévole submission result:', result);
    setIsLoading(false);
    setSubmitted(true);
    window.scrollTo(0, 0);
  };
  const scrollToForm = () => {
    document.getElementById('volunteer-form')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  if (submitted) {
    return <Layout data-id="element-779">
        <div className="min-h-[60vh] flex items-center justify-center bg-gray-50 px-4 py-12" data-id="element-780">
          <Card className="max-w-md w-full text-center p-8" data-id="element-781">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-782">
              <CheckCircle2 className="w-8 h-8 text-green-600" data-id="element-783" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-784">
              Merci de votre engagement !
            </h2>
            <p className="text-gray-600 mb-8" data-id="element-785">
              Votre proposition de bénévolat a bien été enregistrée. L'équipe
              ADCC est ravie de voir votre intérêt pour notre cause. Nous vous
              recontacterons rapidement pour échanger sur les missions
              possibles.
            </p>
            <Button onClick={() => window.location.href = '/'} variant="outline" data-id="element-786">
              Retour à l'accueil
            </Button>
          </Card>
        </div>
      </Layout>;
  }
  return <Layout data-id="element-787">
      <SEO title="Devenir Bénévole - Rejoignez l'ADCC Bénin" description="Devenez bénévole pour l'ADCC et contribuez à l'insertion professionnelle digne au Bénin. Rejoignez une équipe engagée et solidaire." keywords="bénévolat Bénin, volontariat Cotonou, association humanitaire Bénin, engagement social, insertion professionnelle, bénévolat compétences" url="https://adcc.africa/benevoles" data-id="element-788" />

      {/* BLOC 1 — HERO */}
      <div className="bg-[#2C3E50] text-white py-20 relative overflow-hidden" data-id="element-789">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none" data-id="element-790">
          <div className="absolute top-10 left-10 transform rotate-12" data-id="element-791">
            <Heart className="w-32 h-32" data-id="element-792" />
          </div>
          <div className="absolute bottom-10 right-10 transform -rotate-12" data-id="element-793">
            <Users className="w-40 h-40" data-id="element-794" />
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-795">
          <div className="inline-flex items-center justify-center p-3 bg-white/10 rounded-full mb-6 animate-pulse" data-id="element-796">
            <Heart className="w-8 h-8 text-red-400 fill-current" data-id="element-797" />
          </div>
          <h1 className="text-3xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-798">
            Rejoignez le programme de bénévoles ADCC
          </h1>
          <p className="text-xl md:text-2xl text-blue-200 mb-8 font-medium" data-id="element-799">
            Engagez-vous concrètement pour l'emploi et la formation au Bénin.
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-8 max-w-2xl mx-auto border border-white/20" data-id="element-800">
            <p className="text-lg text-gray-100 italic" data-id="element-801">
              "Vous avez du temps, de l'énergie et l'envie d'avoir un impact ?
              ADCC recrute son comité d'action 2026 à Cotonou."
            </p>
          </div>
          <Button onClick={scrollToForm} size="lg" className="bg-[#1E88E5] hover:bg-[#1976D2] text-white px-8 py-4 text-lg rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 gap-2" data-id="element-802">
            👉 Rejoindre le programme
          </Button>
        </div>
      </div>

      {/* BLOC 2 — POURQUOI DEVENIR BÉNÉVOLE ADCC */}
      <section className="py-16 bg-white" data-id="element-803">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-804">
          <div className="text-center mb-12" data-id="element-805">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-806">
              Pourquoi devenir bénévole ADCC ?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto" data-id="element-807">
              Les bénévoles ADCC jouent un rôle essentiel dans le déploiement de
              nos formations et événements.
            </p>
          </div>

          <div className="bg-blue-50 rounded-2xl p-8 md:p-12 max-w-4xl mx-auto" data-id="element-808">
            <h3 className="text-xl font-bold text-[#2C3E50] mb-6 text-center" data-id="element-809">
              Vous contribuez directement :
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-810">
              {["à l'insertion professionnelle", "à l'autonomisation des femmes", 'au développement de compétences locales', 'à la réussite des ateliers ADCC'].map((item, idx) => <div key={idx} className="flex items-center bg-white p-4 rounded-lg shadow-sm" data-id="element-811">
                  <div className="flex-shrink-0 mr-4" data-id="element-812">
                    <CheckCircle2 className="w-6 h-6 text-green-500" data-id="element-813" />
                  </div>
                  <span className="text-gray-800 font-medium" data-id="element-814">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* BLOC 3 — TES MISSIONS */}
      <section className="py-16 bg-gray-50" data-id="element-815">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-816">
          <div className="text-center mb-12" data-id="element-817">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-818">
              Tes missions
            </h2>
            <p className="text-lg text-gray-600" data-id="element-819">
              Des actions concrètes sur le terrain
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-820">
            {[{
            title: 'Accueillir et accompagner',
            desc: 'les bénéficiaires lors des formations',
            icon: Users,
            color: 'text-blue-600',
            bg: 'bg-blue-50'
          }, {
            title: "Diffuser l'information",
            desc: 'dans les quartiers et communautés',
            icon: Megaphone,
            color: 'text-purple-600',
            bg: 'bg-purple-50'
          }, {
            title: 'Animer nos événements',
            desc: 'musique, repas, festivités',
            icon: PartyPopper,
            color: 'text-yellow-600',
            bg: 'bg-yellow-50'
          }, {
            title: 'Assurer la logistique',
            desc: 'des ateliers et festivals ADCC',
            icon: Package,
            color: 'text-green-600',
            bg: 'bg-green-50'
          }].map((mission, idx) => <Card key={idx} className="text-center hover:shadow-lg transition-shadow h-full border-none shadow-md" data-id="element-821">
                <CardContent className="pt-8 pb-6 flex flex-col items-center h-full" data-id="element-822">
                  <div className={`inline-flex items-center justify-center p-4 ${mission.bg} rounded-full mb-4`} data-id="element-823">
                    <mission.icon className={`w-8 h-8 ${mission.color}`} data-id="element-824" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-825">
                    {mission.title}
                  </h3>
                  <p className="text-gray-600 text-sm" data-id="element-826">{mission.desc}</p>
                </CardContent>
              </Card>)}
          </div>
        </div>
      </section>

      {/* BLOC 4 — PROFIL RECHERCHÉ */}
      <section className="py-16 bg-white" data-id="element-827">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-828">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-829">
            <div data-id="element-830">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-831">
                Profil recherché
              </h2>
              <p className="text-xl text-[#1E88E5] font-medium mb-8" data-id="element-832">Tu es :</p>

              <div className="space-y-6" data-id="element-833">
                {[{
                text: "motivé·e par l'impact social",
                icon: Heart
              }, {
                text: 'disponible ponctuellement',
                icon: Clock
              }, {
                text: 'fiable et organisé·e',
                icon: Shield
              }, {
                text: 'chaleureux·se',
                icon: Sparkles
              }, {
                text: 'engagé·e dans les valeurs ADCC',
                icon: Award
              }].map((item, idx) => <div key={idx} className="flex items-center gap-4" data-id="element-834">
                    <div className="p-2 bg-gray-100 rounded-lg text-[#2C3E50]" data-id="element-835">
                      <item.icon className="w-5 h-5" data-id="element-836" />
                    </div>
                    <span className="text-lg text-gray-700 font-medium" data-id="element-837">
                      {item.text}
                    </span>
                  </div>)}
              </div>
            </div>

            <div className="bg-[#2C3E50] text-white p-10 rounded-2xl shadow-xl text-center relative overflow-hidden" data-id="element-838">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#1E88E5] rounded-full filter blur-3xl opacity-20 transform translate-x-10 -translate-y-10" data-id="element-839"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#1E88E5] rounded-full filter blur-3xl opacity-20 transform -translate-x-10 translate-y-10" data-id="element-840"></div>

              <h3 className="text-2xl font-bold mb-8 relative z-10" data-id="element-841">
                Nos Valeurs
              </h3>
              <div className="space-y-6 relative z-10" data-id="element-842">
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/10" data-id="element-843">
                  <span className="text-xl font-bold tracking-wide" data-id="element-844">
                    DIGNITÉ
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/10" data-id="element-845">
                  <span className="text-xl font-bold tracking-wide" data-id="element-846">
                    COMPÉTENCE
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg border border-white/10" data-id="element-847">
                  <span className="text-xl font-bold tracking-wide" data-id="element-848">
                    CRÉATIVITÉ
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOC 5 — CE QUE NOUS OFFRONS */}
      <section className="py-16 bg-gray-50" data-id="element-849">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-850">
          <div className="text-center mb-12" data-id="element-851">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-852">
              Ce que nous t'offrons
            </h2>
            <p className="text-lg text-gray-600" data-id="element-853">
              Parce que ton engagement mérite d'être valorisé
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-854">
            {[{
            text: 'Tenue officielle ADCC',
            icon: Star
          }, {
            text: 'Indemnité de présence',
            icon: Gift
          }, {
            text: 'Remboursement transport',
            icon: MapPin
          }, {
            text: 'Repas sur place',
            icon: Heart
          }, {
            text: 'Attestation de bénévolat valorisable sur CV',
            icon: Award
          }, {
            text: 'Expérience terrain concrète',
            icon: Users
          }].map((offer, idx) => <div key={idx} className="bg-white border-2 border-gray-100 hover:border-[#1E88E5] rounded-xl p-6 transition-colors flex flex-col items-center text-center group" data-id="element-855">
                <div className="p-3 bg-blue-50 rounded-full text-[#1E88E5] mb-4 group-hover:bg-[#1E88E5] group-hover:text-white transition-colors" data-id="element-856">
                  <offer.icon className="w-6 h-6" data-id="element-857" />
                </div>
                <span className="font-semibold text-gray-800" data-id="element-858">
                  {offer.text}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* BLOC 6 — LOCALISATION */}
      <section className="py-12 bg-[#2C3E50] text-white" data-id="element-859">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-860">
          <div className="inline-flex items-center justify-center p-4 bg-white/10 rounded-full mb-6" data-id="element-861">
            <MapPin className="w-8 h-8 text-[#1E88E5]" data-id="element-862" />
          </div>
          <h2 className="text-2xl font-bold mb-2" data-id="element-863">Localisation</h2>
          <p className="text-xl mb-4" data-id="element-864">📍 Cotonou, Bénin</p>
          <p className="text-gray-300 bg-white/10 inline-block px-4 py-2 rounded-full text-sm" data-id="element-865">
            Engagement flexible selon disponibilités
          </p>
        </div>
      </section>

      {/* BLOC 7 — FORMULAIRE */}
      <section id="volunteer-form" className="py-20 bg-white" data-id="element-866">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-867">
          <Card className="shadow-2xl border-t-4 border-t-[#1E88E5]" data-id="element-868">
            <CardContent className="p-8 md:p-12" data-id="element-869">
              <div className="text-center mb-10" data-id="element-870">
                <h2 className="text-3xl font-bold text-gray-900" data-id="element-871">
                  Rejoindre le programme bénévoles
                </h2>
                <p className="text-gray-600 mt-2" data-id="element-872">
                  Remplissez ce formulaire pour faire partie de l'aventure.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-873">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-874">
                  <div data-id="element-875">
                    <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-876">
                      Nom *
                    </label>
                    <input type="text" id="nom" name="nom" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="Votre nom" data-id="element-877" />
                  </div>
                  <div data-id="element-878">
                    <label htmlFor="prenom" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-879">
                      Prénom *
                    </label>
                    <input type="text" id="prenom" name="prenom" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="Votre prénom" data-id="element-880" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-881">
                  <div data-id="element-882">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-883">
                      Email *
                    </label>
                    <input type="email" id="email" name="email" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="votre@email.com" data-id="element-884" />
                  </div>
                  <div data-id="element-885">
                    <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-886">
                      Téléphone *
                    </label>
                    <input type="tel" id="telephone" name="telephone" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="+229..." data-id="element-887" />
                  </div>
                </div>

                <div data-id="element-888">
                  <label htmlFor="quartier" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-889">
                    Quartier de résidence *
                  </label>
                  <input type="text" id="quartier" name="quartier" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="Ex: Haie Vive, Cotonou" data-id="element-890" />
                </div>

                <div data-id="element-891">
                  <label htmlFor="disponibilite" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-892">
                    Disponibilités *
                  </label>
                  <select id="disponibilite" name="disponibilite" required className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border bg-white" data-id="element-893">
                    <option value="" data-id="element-894">Vos disponibilités ?</option>
                    <option value="ponctuelle" data-id="element-895">Ponctuelle (événements)</option>
                    <option value="weekend" data-id="element-896">Week-ends</option>
                    <option value="semaine" data-id="element-897">En semaine</option>
                    <option value="reguliere" data-id="element-898">Régulière et soutenue</option>
                  </select>
                </div>

                <div data-id="element-899">
                  <label htmlFor="motivation" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-900">
                    Motivation (court texte) *
                  </label>
                  <textarea id="motivation" name="motivation" required rows={3} className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#1E88E5] focus:ring-[#1E88E5] p-3 border" placeholder="Pourquoi souhaitez-vous rejoindre l'ADCC ?" data-id="element-901"></textarea>
                </div>

                <div data-id="element-902">
                  <label htmlFor="cv" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-903">
                    CV ou Document (Optionnel)
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-[#1E88E5] transition-colors cursor-pointer bg-gray-50" data-id="element-904">
                    <div className="space-y-1 text-center" data-id="element-905">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" data-id="element-906" />
                      <div className="flex text-sm text-gray-600" data-id="element-907">
                        <label htmlFor="cv-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-[#1E88E5] hover:text-[#1976D2] focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#1E88E5]" data-id="element-908">
                          <span data-id="element-909">Télécharger un fichier</span>
                          <input id="cv-upload" name="cv" type="file" className="sr-only" accept=".pdf,.doc,.docx,.jpg,.png" data-id="element-910" />
                        </label>
                        <p className="pl-1" data-id="element-911">ou glisser-déposer</p>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-912">
                        PDF, DOC, IMG jusqu'à 5MB
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4" data-id="element-913">
                  <Button type="submit" className="w-full text-lg py-6 gap-2" size="lg" disabled={isLoading} data-id="element-914">
                    👉 Je rejoins ADCC
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* BLOC 8 — RÉASSURANCE */}
      <section className="py-12 bg-gray-50 border-t border-gray-200" data-id="element-915">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-916">
          <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100" data-id="element-917">
            <p className="text-lg text-gray-700 font-medium" data-id="element-918">
              "ADCC forme et accompagne ses bénévoles. Aucune expérience
              préalable obligatoire. Esprit d'équipe essentiel."
            </p>
          </div>
        </div>
      </section>

      {/* BLOC 9 — CONTACT + CTA FINAL */}
      <section className="py-16 bg-[#2C3E50] text-white" data-id="element-919">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-920">
          <div className="flex flex-col md:flex-row justify-center gap-8 mb-10 text-gray-300" data-id="element-921">
            <div className="flex items-center gap-2" data-id="element-922">
              <Mail className="w-5 h-5 text-[#1E88E5]" data-id="element-923" />
              <span data-id="element-924">contact@adcc.africa</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-925">
              <MapPin className="w-5 h-5 text-[#1E88E5]" data-id="element-926" />
              <span data-id="element-927">Cotonou, Bénin</span>
            </div>
          </div>

          <h2 className="text-2xl md:text-3xl font-bold mb-8" data-id="element-928">
            Rejoignez le comité d'action 2026. Chaque engagement compte.
          </h2>

          <Button onClick={scrollToForm} size="lg" className="bg-[#1E88E5] hover:bg-[#1976D2] text-white px-10 py-4 text-lg rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 gap-2" data-id="element-929">
            👉 Devenir bénévole
          </Button>
        </div>
      </section>
    </Layout>;
}